import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { surface, tone } from '../timeclock/tcFormat';
import { addDays, locale, mondayOf, parseDate, teamError, teamPost } from './teamFormat';

const WEEKS = 12;

/**
 * Duty weeks: the support colleague who is VIP of the day Monday-Friday also
 * has the weekend duty after it. Jeroen plans the weeks; colleagues swap a
 * weekend for a weekend among themselves (the one asked must agree), without
 * management. The VIP weeks themselves never move.
 */
const TeamDutyTab = ({ t, lang, theme, token, me, staff, reloadKey, notify, failed, onChanged }) => {
    const [from, setFrom] = useState(mondayOf(me.today));
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [again, setAgain] = useState(0);
    const [busy, setBusy] = useState(false);
    const [swapFor, setSwapFor] = useState(null);   // week I give away
    const [swapWith, setSwapWith] = useState('');
    const [swapNote, setSwapNote] = useState('');

    useEffect(() => {
        let alive = true;
        teamPost('duty', token, { from, weeks: WEEKS }).then((response) => {
            if (!alive) return;
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, from, reloadKey, again, failed]);

    const run = (action, body, done) => {
        setBusy(true);
        teamPost(action, token, body).then((response) => {
            setBusy(false);
            if (response?.success) {
                if (done) notify(done, true);
                setAgain((k) => k + 1);
                if (onChanged) onChanged();
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    const s = surface(theme);
    const loc = locale(lang);
    const support = staff.filter((p) => p.coverage === 'SUPPORT');
    // Weekend duty: the support colleagues, plus whoever has it now when that is someone else.
    const weekendPeople = (w) => (w.weekend && !support.some((p) => p.id === w.weekend.id)
        ? [...support, { id: w.weekend.id, name: w.weekend.name }] : support);
    const range = (w) => parseDate(w.monday).toLocaleDateString(loc, { day: 'numeric', month: 'short' }) + ' – ' + parseDate(w.sunday).toLocaleDateString(loc, { day: 'numeric', month: 'short' });
    const weekLabel = (y, w) => t('team_week_short', 'wk') + ' ' + w + (y !== parseInt(me.today.substring(0, 4), 10) ? ' ' + y : '');
    const passed = (w) => w.sunday < me.today;

    if (error) return <Typography sx={{ color: tone.bad, fontSize: 13.5 }}>{error}</Typography>;
    if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;

    const waiting = data.swaps.filter((x) => x.status === 'PENDING');
    const swapTargets = data.weeks.filter((w) => w.weekend && w.weekend.id !== me.staff.id && !passed(w) && swapFor && w.iso_week !== swapFor.iso_week);

    return (
        <Box sx={{ maxWidth: 900 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                {t('team_duty_note', 'VIP of the day Monday to Friday plus the weekend duty after it. Jeroen plans; colleagues swap only the weekend among themselves.')}
                {data.can_edit_weekend ? ' ' + t('team_weekend_mgmt_note', 'Management can also change a weekend directly; the calendar, the shared Google calendar and the Friday Discord message follow.') : ''}
            </Typography>

            {waiting.length > 0 && (
                <Box sx={{ ...s.card, p: 2, mb: 2, borderLeft: '4px solid ' + tone.info }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>{t('team_swaps_waiting', 'Weekend swaps waiting')}</Typography>
                    {waiting.map((x) => (
                        <Box key={x.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.75, borderTop: s.line }}>
                            <Typography sx={{ fontSize: 13.5, flex: '1 1 320px' }}>
                                {t('team_swap_line', {
                                    defaultValue: '{{a}} offers the weekend of {{wa}} in exchange for the weekend of {{wb}} ({{b}}).',
                                    a: x.a.staff.name, wa: weekLabel(x.a.iso_year, x.a.iso_week), wb: weekLabel(x.b.iso_year, x.b.iso_week), b: x.b.staff.name,
                                })}
                                {x.remark ? ' "' + x.remark + '"' : ''}
                            </Typography>
                            {x.mine_to_answer && (
                                <>
                                    <Button size="small" color="error" disabled={busy} onClick={() => run('answerSwap', { id: x.id, accept: false }, t('team_swap_declined', 'Swap declined'))} sx={{ textTransform: 'none' }}>
                                        {t('team_decline', 'Decline')}
                                    </Button>
                                    <Button size="small" variant="contained" disableElevation disabled={busy} onClick={() => run('answerSwap', { id: x.id, accept: true }, t('team_swap_done', 'Weekends swapped; both get a confirmation.'))} sx={{ textTransform: 'none' }}>
                                        {t('team_accept', 'Agree')}
                                    </Button>
                                </>
                            )}
                            {!x.mine_to_answer && x.mine_to_cancel && (
                                <Button size="small" disabled={busy} onClick={() => run('cancelSwap', { id: x.id }, t('team_cancelled', 'Cancelled'))} sx={{ textTransform: 'none' }}>
                                    {t('team_withdraw', 'Withdraw')}
                                </Button>
                            )}
                            {!x.mine_to_answer && !x.mine_to_cancel && (
                                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('team_swap_wait', { defaultValue: 'waiting for {{b}}', b: x.b.staff.name })}</Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconButton size="small" onClick={() => setFrom(addDays(from, -7 * WEEKS))}><ChevronLeft /></IconButton>
                <IconButton size="small" onClick={() => setFrom(addDays(from, 7 * WEEKS))}><ChevronRight /></IconButton>
                {from !== mondayOf(me.today) && <Button size="small" onClick={() => setFrom(mondayOf(me.today))} sx={{ textTransform: 'none' }}>{t('team_today', 'Today')}</Button>}
                <Box sx={{ flex: 1 }} />
                {data.can_plan && (
                    <>
                        <Button size="small" variant="outlined" disabled={busy} sx={{ textTransform: 'none' }}
                            onClick={() => {
                                setBusy(true);
                                teamPost('importDuty', token, { from }).then((response) => {
                                    setBusy(false);
                                    if (response?.success) {
                                        notify(t('team_duty_imported', { defaultValue: '{{n}} weeks taken over from the weekly schedule.', n: response.data.imported }), true);
                                        setAgain((k) => k + 1);
                                    } else {
                                        notify(teamError(t, response), false);
                                    }
                                });
                            }}>
                            {t('team_duty_import', 'Take over from the weekly schedule')}
                        </Button>
                        <Button size="small" variant="contained" disableElevation disabled={busy} sx={{ textTransform: 'none' }}
                            onClick={() => {
                                if (!window.confirm(t('team_duty_fill_confirm', 'Fill the empty weeks shown by rotation? Planned weeks stay as they are.'))) return;
                                setBusy(true);
                                teamPost('fillDuty', token, { from, weeks: WEEKS }).then((response) => {
                                    setBusy(false);
                                    if (response?.success) {
                                        notify(t('team_duty_filled', { defaultValue: '{{n}} weeks filled in.', n: response.data.filled }), true);
                                        setAgain((k) => k + 1);
                                    } else {
                                        notify(teamError(t, response), false);
                                    }
                                });
                            }}>
                            {t('team_duty_fill', 'Fill empty weeks (rotation)')}
                        </Button>
                    </>
                )}
            </Box>
            {data.can_plan && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1 }}>
                    {t('team_duty_fill_note', 'Rotation: the support colleague whose last duty week is longest ago is next; someone away three or more days of that week is skipped. You can still change every week afterwards.')}
                </Typography>
            )}
            <Box sx={{ ...s.card }}>
                {data.weeks.map((w) => {
                    const mine = w.weekend && w.weekend.id === me.staff.id;
                    const swapped = w.vip && w.weekend && w.vip.id !== w.weekend.id;
                    return (
                        <Box key={w.iso_year + '-' + w.iso_week} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25, borderTop: s.line, flexWrap: 'wrap',
                            opacity: passed(w) ? 0.55 : 1, backgroundColor: mine ? (theme === 'dark' ? 'rgba(122,86,194,0.12)' : 'rgba(122,86,194,0.06)') : undefined }}>
                            <Box sx={{ width: 120 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{weekLabel(w.iso_year, w.iso_week)}</Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{range(w)}</Typography>
                            </Box>
                            <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                                {data.can_plan && !passed(w) ? (
                                    <TextField select size="small" value={w.vip ? w.vip.id : ''} disabled={busy} label={t('team_vip', 'VIP of the day')}
                                        onChange={(e) => run('assignDuty', { iso_year: w.iso_year, iso_week: w.iso_week, staff_id: e.target.value || null })}
                                        sx={{ minWidth: 200, '& .MuiInputBase-root': { height: 36 } }}>
                                        <MenuItem value="">—</MenuItem>
                                        {support.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                                    </TextField>
                                ) : (
                                    <Typography sx={{ fontSize: 13.5 }}>
                                        {w.vip ? t('team_vip', 'VIP of the day') + ': ' + w.vip.name : <Box component="span" sx={{ color: 'text.secondary' }}>{t('team_not_planned', 'not planned yet')}</Box>}
                                    </Typography>
                                )}
                                {data.can_edit_weekend && !passed(w) ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                        <TextField select size="small" value={w.weekend ? w.weekend.id : ''} disabled={busy} label={t('team_weekend_duty', 'Weekend duty')}
                                            onChange={(e) => e.target.value && run('setWeekend', { iso_year: w.iso_year, iso_week: w.iso_week, staff_id: e.target.value },
                                                t('team_weekend_set', 'Weekend duty changed; both colleagues get a mail.'))}
                                            sx={{ minWidth: 200, '& .MuiInputBase-root': { height: 36 } }}>
                                            {!w.weekend && <MenuItem value="">—</MenuItem>}
                                            {weekendPeople(w).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                                        </TextField>
                                        {swapped && <Typography sx={{ fontSize: 12.5, color: tone.info }}>{t('team_weekend_other', 'other than the VIP')}</Typography>}
                                    </Box>
                                ) : w.weekend && (
                                    <Typography sx={{ fontSize: 12.5, color: swapped ? tone.info : 'text.secondary', mt: 0.25 }}>
                                        {t('team_weekend_duty', 'Weekend duty')}: {w.weekend.name}{swapped ? ' · ' + t('team_swapped', 'swapped') : ''}
                                    </Typography>
                                )}
                            </Box>
                            {mine && !passed(w) && (
                                <Button size="small" variant="outlined" onClick={() => { setSwapFor(w); setSwapWith(''); setSwapNote(''); }} sx={{ textTransform: 'none' }}>
                                    {t('team_swap_weekend', 'Swap weekend')}
                                </Button>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {swapFor && (
                <Box sx={{ ...s.card, p: 2, mt: 2 }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>
                        {t('team_swap_title', { defaultValue: 'Swap your weekend of {{w}}', w: weekLabel(swapFor.iso_year, swapFor.iso_week) + ' (' + range(swapFor) + ')' })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField select size="small" label={t('team_swap_with', 'For the weekend of')} value={swapWith} onChange={(e) => setSwapWith(e.target.value)} sx={{ minWidth: 260 }}>
                            {swapTargets.map((w) => (
                                <MenuItem key={w.iso_year + '-' + w.iso_week} value={w.iso_year + '-' + w.iso_week}>
                                    {weekLabel(w.iso_year, w.iso_week)} · {range(w)} · {w.weekend.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField size="small" label={t('team_remark', 'Remark')} value={swapNote} onChange={(e) => setSwapNote(e.target.value)} sx={{ flex: '1 1 200px' }} />
                        <Button variant="contained" disableElevation disabled={!swapWith || busy} sx={{ textTransform: 'none' }}
                            onClick={() => {
                                const [yb, wb] = swapWith.split('-').map(Number);
                                run('proposeSwap', { year_a: swapFor.iso_year, week_a: swapFor.iso_week, year_b: yb, week_b: wb, remark: swapNote },
                                    t('team_swap_asked', 'Asked; your colleague gets a mail and has to agree.'));
                                setSwapFor(null);
                            }}>
                            {t('team_swap_ask', 'Ask to swap')}
                        </Button>
                        <Button onClick={() => setSwapFor(null)} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TeamDutyTab;
